import { chakra, HTMLChakraProps } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import ReactMarkdown, { ReactMarkdownProps } from 'react-markdown';

import { convertTimestampsToLocalizedTime } from '@/utils/timestampToLocalizedTime';

const ChakraMarkdown = chakra(ReactMarkdown, {
  baseStyle: {
    fontFamily: 'body',
    p: {
      mt: '2rem',
      mb: '2rem',
      ':first-of-type': {
        mt: 0,
      },
      ':last-of-type': {
        mb: 0,
      },
    },
    img: {
      maxW: '100%',
      height: 'auto',
      my: '2.5rem',
      mx: 0,
      w: '100%',
      display: 'inline-block',
    },
    'h1, h2, h3, h4, h5, h6': {
      my: '2.5rem',
      fontFamily: 'heading',
    },
    h1: {
      fontSize: '3rem',
      lineHeight: '3.75rem',
    },
    h2: {
      fontSize: '2.25rem',
      lineHeight: '3rem',
    },
    h3: {
      fontSize: '2rem',
      lineHeight: '2.5rem',
    },
    h4: {
      fontSize: '1.75rem',
      lineHeight: '2.25rem',
    },
    a: {
      color: 'blue.500',
      ':hover': {
        textDecoration: 'underline',
      },
      ':visited': {
        color: 'blue.500',
      },
    },
    blockquote: {
      p: {
        quotes: "'“' '”' '‘' '’'",
        '::before': {
          content: 'open-quote',
        },
        '::after': {
          content: 'close-quote',
        },
      },
    },
    'ul, ol': {
      pl: '2.5rem',
      mx: 0,
      my: '1.25rem',
      p: {
        m: 0,
      },
    },
    ul: {
      listStyle: 'disc',
    },
    '.videoIframeWrapper': {
      overflow: 'hidden',
      pt: '56.25%',
      position: 'relative',
      my: '2.5rem',
      mx: 0,
      iframe: {
        border: 0,
        height: '100%',
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
      },
    },
  },
});

export const LectureAbstractMarkdown: FC<ReactMarkdownProps & HTMLChakraProps<'div'>> = ({ source, ...rest }) => {
  // we are modifing source since we are using some custom markdown syntax for some elements (timestamps, ...)
  const modifiedSource = useMemo(() => convertTimestampsToLocalizedTime(source), [source]);

  if (!source) {
    return null;
  }

  return <ChakraMarkdown includeNodeIndex source={modifiedSource} escapeHtml={false} linkTarget="_blank" {...rest} />;
};
